import { useState, useEffect, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Loader2, Bot, User, Sparkles, MessageSquare, BookOpen, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { AGENT_CATALOG } from "@/lib/agent-catalog";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { marked } from "marked";
import { API_BASE_URL } from "@/config";

function parseMobileMarkdown(content: string, hasVisualSources = false): string {
  if (!content) return "";
  try {
    let clean = content;
    if (hasVisualSources) {
      clean = clean.replace(/(?:\r?\n\s*(?:---|___|\*\*\*)\s*)?(?:\r?\n|^)\s*(?:#{1,6}\s*)?\s*\bFuentes(?:\s+(?:consultadas|citadas))?\b\s*:?[\s\S]*$/gi, "").trim();
    }
    const raw = marked.parse(clean, { breaks: true, gfm: true }) as string;
    const withRefs = raw.replace(
      /(?<!<[^>]*)\[Ref:\s*([^\]]+)\](?![^<]*>)/gi,
      `<span class="inline-flex items-center justify-center font-semibold text-[10.5px] px-1.5 py-0.5 mx-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-300 select-none align-baseline">[Ref: $1]</span>`
    );
    return withRefs.replace(
      /(?<!<[^>]*)\[(\d+)\](?![^<]*>)/g,
      `<span class="inline-flex items-center justify-center font-bold text-[11px] px-1.5 py-0.5 mx-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 select-none">[$1]</span>`
    );
  } catch {
    return content;
  }
}

export default function MobileAgentRunPage() {
  const { agentSlug } = useParams<{ agentSlug: string }>();
  const { user } = useAuth();
  const apiClient = useApiClient();
  const { runs, startChatRun } = useAgentExecution();
  const { openDocument } = useDocumentViewer();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const agent = agentSlug ? AGENT_CATALOG[agentSlug] : undefined;

  // Logic to find an active or recent run for this agent
  const currentRun = Object.values(runs)
    .filter(r => {
      if (agentSlug === "specialized") return r.id.startsWith("specialized-");
      return r.id.includes(agentSlug || "");
    })
    .sort((a, b) => b.id.localeCompare(a.id))[0] || null;

  useEffect(() => {
    // Scroll to bottom when messages or trace steps update
    const container = document.querySelector('[data-radix-scroll-area-viewport]');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [currentRun?.messages, currentRun?.traceSteps, currentRun?.status]);

  if (!agentSlug || !agent) {
    return <Navigate to="/dashboard/agents/mobile" replace />;
  }

  // Dedicated full-featured agents should not be degraded to generic chat runner
  if (agent.path && agentSlug !== "specialized") {
    return <Navigate to={agent.path} replace />;
  }

  const handleSend = async () => {
    if (!input.trim() || !agentSlug) return;

    const runId = `${agentSlug}-${Date.now()}`;
    const messageText = input;
    setInput("");

    // For mobile agents, we default to specialized or chat run
    try {
      await startChatRun(
        runId,
        currentRun?.messages || [],
        messageText,
        "", // instructions
        null, // file
        user?.id || null,
        apiClient
      );
    } catch (err) {
      console.error("Error starting run:", err);
    }
  };

  const Icon = agent.icon;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-white/80 backdrop-blur-md border-b flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3">
          <Link to={`/dashboard/agents/mobile/${agentSlug}`} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </Link>
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${agent.gradient} flex items-center justify-center shadow-sm`}>
              <Icon className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-slate-900 leading-tight">{agent.name}</h1>
              <div className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${currentRun?.status === "streaming" ? "bg-green-500 animate-pulse" : "bg-slate-300"}`} />
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                  {currentRun?.status === "streaming" ? "Procesando" : "Agente Mobile"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <Link to={`/dashboard/agents/mobile/${agentSlug}/history`}>
          <Button variant="ghost" size="icon" className="text-slate-400">
            <Sparkles className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6 pb-24">
          {!currentRun && (
            <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
              <div className={`h-20 w-20 rounded-[2rem] bg-gradient-to-br ${agent.gradient} flex items-center justify-center shadow-lg opacity-20`}>
                <Icon className="h-10 w-10 text-white" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-slate-800">¿Cómo puedo ayudarte?</h2>
                <p className="text-sm text-slate-500 max-w-[240px] mx-auto">
                  El agente {agent.name} está listo para procesar tu consulta jurídica.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center pt-4">
                <Badge variant="secondary" className="px-3 py-1 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => setInput("Análisis de riesgos")}>Análisis de riesgos</Badge>
                <Badge variant="secondary" className="px-3 py-1 cursor-pointer hover:bg-slate-200 transition-colors" onClick={() => setInput("Resumen jurídico")}>Resumen jurídico</Badge>
              </div>
            </div>
          )}

          {currentRun?.messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300 w-full min-w-0`}>
              <div className={`relative max-w-[90%] sm:max-w-[85%] rounded-2xl px-3.5 sm:px-4 py-3 text-sm shadow-sm min-w-0 break-words [overflow-wrap:anywhere] overflow-hidden ${msg.role === "user"
                ? "bg-indigo-600 text-white rounded-tr-none"
                : "bg-white text-slate-800 border border-slate-200/80 rounded-tl-none shadow-xs"
                }`}>
                {msg.role === "user" ? (
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                ) : (
                  (() => {
                    const rawSources = msg.sourceDetails?.length
                      ? msg.sourceDetails
                      : msg.metadata?.source_details?.length
                        ? msg.metadata.source_details
                        : msg.metadata?.sources?.length
                          ? msg.metadata.sources
                          : msg.sources?.length
                            ? msg.sources
                            : [];

                    const normalizedSources = rawSources.map((s: any, idx: number) => ({
                      name: typeof s === "string" ? s : (s.name || s.title || `Fuente [${idx + 1}]`),
                      url: typeof s === "string" ? "" : (s.raw_url || s.url || s.storage_url || ""),
                      excerpt: typeof s === "string" ? "" : (s.referenced_content || s.excerpt || s.snippet || ""),
                      pageNumber: typeof s === "string" ? undefined : (s.pageNumber || s.page),
                      article: typeof s === "string" ? undefined : s.article,
                      index: typeof s === "string" ? (idx + 1) : (s.index ?? (idx + 1))
                    }));

                    return (
                      <div>
                        <div
                          className="prose prose-slate prose-sm max-w-none text-slate-800 leading-relaxed [overflow-wrap:anywhere]"
                          dangerouslySetInnerHTML={{ __html: parseMobileMarkdown(msg.content, normalizedSources.length > 0) }}
                        />
                        {/* Citations/Sources section if present */}
                        {normalizedSources.length > 0 && (
                          <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-col gap-1.5">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                              <BookOpen className="h-3 w-3 text-indigo-500" />
                              <span>Fuentes y Citas Jurídicas ({normalizedSources.length})</span>
                            </div>
                            <div className="flex flex-col gap-1.5">
                              {normalizedSources.map((source: any, sIdx: number) => {
                                const cleanName = String(source.name).replace(/\.pdf$/i, "").trim();
                                const docUrl = source.url || `${API_BASE_URL}/api/v1/documents/raw/${encodeURIComponent(cleanName)}.pdf`;
                                return (
                                  <button
                                    key={sIdx}
                                    type="button"
                                    onClick={() => {
                                      openDocument(docUrl, cleanName, source.excerpt || source.article || cleanName, source.pageNumber || null);
                                    }}
                                    className="flex items-center justify-between text-left gap-1.5 text-[11px] font-medium bg-white hover:bg-indigo-50 text-slate-700 px-2.5 py-1.5 rounded-lg border border-slate-200 transition-colors shadow-2xs"
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      <span className="font-bold text-indigo-600 bg-indigo-50 px-1 rounded shrink-0">[{source.index || sIdx + 1}]</span>
                                      <span className="truncate flex-1">{source.name}</span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {source.pageNumber && (
                                        <span className="text-[9.5px] font-semibold bg-slate-100 text-slate-600 px-1 rounded">Pág. {source.pageNumber}</span>
                                      )}
                                      <ExternalLink className="h-3 w-3 text-slate-400" />
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
                <div className={`absolute bottom-[-18px] text-[9px] font-bold uppercase tracking-tighter ${msg.role === "user" ? "right-0 text-slate-400" : "left-0 text-slate-400"}`}>
                  {msg.role === "user" ? "Tú" : agent.name}
                </div>
              </div>
            </div>
          ))}

          {/* Trace Steps / Loading */}
          {currentRun?.status === "streaming" && (
            <div className="flex flex-col gap-2 pt-2 animate-in fade-in duration-500">
              <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-2xl space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Loader2 className="h-3 w-3 animate-spin text-indigo-600" />
                  <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">Ejecutando razonamiento</span>
                </div>
                {currentRun.traceSteps.map((step, i) => (
                  <div key={i} className={`flex items-start gap-2 text-xs transition-all duration-300 ${step.status === "running" ? "text-slate-800 font-medium" : "text-slate-400"}`}>
                    <span className="flex-shrink-0 mt-0.5">{step.emoji}</span>
                    <span>{step.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 bg-white/80 backdrop-blur-md border-t sticky bottom-0 z-10 pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:pb-4">
        <div className="max-w-md mx-auto relative group">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Describe tu requerimiento..."
            className="pr-14 py-7 rounded-[1.5rem] border-slate-200 bg-slate-50/50 focus:bg-white transition-all shadow-inner focus:ring-indigo-500/20"
            disabled={currentRun?.status === "streaming"}
          />
          <Button
            size="icon"
            className={`absolute right-2 top-2 h-10 w-10 rounded-xl transition-all duration-300 shadow-lg ${!input.trim() || currentRun?.status === "streaming"
              ? "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none"
              : "bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105 active:scale-95"
              }`}
            onClick={handleSend}
            disabled={!input.trim() || currentRun?.status === "streaming"}
          >
            {currentRun?.status === "streaming" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </Button>
        </div>
        <p className="text-[9px] text-center text-slate-400 mt-2 uppercase font-bold tracking-widest">
          Potenciado por Antigravity AI • Modelo Mobile 1.0
        </p>
      </div>
    </div>
  );
}
